import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_BASE } from "@admin/constants/route";

/**
 * Gates the admin shell behind a successful 2FA verification.
 *
 *   - If the admin's `two_factor_enabled` is false → pass through (opted-out
 *     non-mandatory admin).
 *   - If `two_factor_satisfied` is true → pass through.
 *   - Otherwise → redirect to /<ADMIN_BASE>/verify carrying the original path
 *     so the user lands back where they were going post-verify.
 *
 * This is UX only. The security boundary is the backend: every /api/admin/*
 * request is refused with 403 {code: TWO_FACTOR_REQUIRED} until the session is
 * verified (see services/login_role.py). That response is surfaced by the
 * shared axios interceptor as an `advoset:two-factor-required` event, which we
 * also route to the verify screen here.
 */
export default function RequireTwoFactor({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onRequired = () => navigate(`${ADMIN_BASE}/verify`, { replace: true, state: { from: location.pathname + location.search } });
    const onStale = () => navigate(`${ADMIN_BASE}/verify`, { state: { from: location.pathname + location.search, stepUp: true } });
    window.addEventListener("advoset:two-factor-required", onRequired);
    window.addEventListener("advoset:two-factor-stale", onStale);
    return () => {
      window.removeEventListener("advoset:two-factor-required", onRequired);
      window.removeEventListener("advoset:two-factor-stale", onStale);
    };
  }, [navigate, location.pathname, location.search]);

  if (loading) return null;
  if (!user || user.role !== "admin") return null;
  if (!user.two_factor_enabled || user.two_factor_satisfied) return children;

  return (
    <Navigate
      to={`${ADMIN_BASE}/verify`}
      replace
      state={{ from: location.pathname + location.search }}
    />
  );
}
